import { isEmpty } from 'lodash'
import { NAMESPACE } from '../utils'
import CustomButton from './CustomButton'
import SearchBar from './SearchBar'

interface PopupUiDropDownListProps {
  dataQaId: string
  handleSearch: (searchTerm: string) => void
  searchPlaceholder: string
  filterOptions: { label: string; value: string; status?: string }[]
  onApply?: () => void
  onCancel?: () => void
  setSelectedOption: React.Dispatch<
    React.SetStateAction<{ label: string; value: string } | null>
  >
  selectedOption: { label: string; value: string } | null
  cancelButtonLabel: string
  applyButtonLabel: string
  showButton: boolean
  optionCss?: string
  selectOption?: boolean
  optionListHeight?: string
  isDisabled?: boolean
  emptyDataMessage?: string
  searchContainerRef?: React.RefObject<HTMLDivElement>
  optionsRef?: React.MutableRefObject<(HTMLLIElement | null)[]>
  activeIndex?: number
  handleSearchKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleOptionKeyDown?: (
    e: React.KeyboardEvent<HTMLLIElement>,
    index: number
  ) => void
  cancelButtonRef?: React.RefObject<HTMLButtonElement>
  applyButtonRef?: React.RefObject<HTMLButtonElement>
}

const PopupUiDropDownList: React.FC<PopupUiDropDownListProps> = ({
  dataQaId,
  handleSearch,
  searchPlaceholder,
  filterOptions,
  onApply,
  setSelectedOption,
  onCancel,
  cancelButtonLabel,
  applyButtonLabel,
  selectedOption,
  showButton,
  optionCss,
  selectOption,
  optionListHeight,
  isDisabled = false,
  emptyDataMessage,
  searchContainerRef,
  optionsRef,
  activeIndex = -1,
  handleSearchKeyDown,
  handleOptionKeyDown,
  cancelButtonRef,
  applyButtonRef,
}) => {
  // Create a custom onKeyDown handler that wraps the parent handler
  const searchKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    handleSearchKeyDown && handleSearchKeyDown(e)
  }

  return (
    <div
      className="flex flex-col h-full justify-between"
      data-qa-id={dataQaId}
      role="dialog"
    >
      <div ref={searchContainerRef}>
        <SearchBar
          placeholder={searchPlaceholder}
          onSearch={handleSearch}
          width="w-full"
          height="h-[2.375rem]"
          showFunnel={false}
          onKeyDown={searchKeyDownHandler}
          autoFocus={true}
        />
      </div>
      <ul
        className={`${optionListHeight} overflow-y-auto mt-1`}
        role="listbox"
        aria-label={`${searchPlaceholder} options`}
      >
        {filterOptions.map(
          (
            option: { label: string; value: string; status?: string },
            index
          ) => (
            <li
              key={option.value}
              className={`${optionCss} ${
                selectOption &&
                (selectedOption?.value === option.value
                  ? 'bg-blue-500 text-white'
                  : 'hover:bg-blue-500')
              } ${
                activeIndex === index ? 'bg-blue-100' : ''
              } cursor-pointer p-2`}
              onClick={() =>
                setSelectedOption(option === selectedOption ? null : option)
              }
              ref={(el) => {
                if (optionsRef) optionsRef.current[index] = el
              }}
              role="option"
              aria-selected={selectedOption?.value === option.value}
              tabIndex={-1}
              onKeyDown={(e) =>
                handleOptionKeyDown && handleOptionKeyDown(e, index)
              }
            >
              <span className="truncate font-light text-base">
                {option.label}
              </span>
              {option?.status && (
                <span
                  className={`truncate font-light text-base ${
                    option?.status === 'Available'
                      ? 'text-green-700'
                      : 'text-red-600'
                  }`}
                >
                  {option?.status}
                </span>
              )}
            </li>
          )
        )}
        {isEmpty(filterOptions) && (
          <li
            className="w-full h-full flex justify-center items-center"
            role="alert"
          >
            <span className="font-light text-xs leading-4">
              {emptyDataMessage ?? NAMESPACE.NO_DATA_FOUND}
            </span>
          </li>
        )}
      </ul>
      {showButton && (
        <div className="flex items-center justify-between mt-2 gap-2">
          <CustomButton
            color="primary"
            size="small"
            isFilled={false}
            widthClass="w-[6.4375rem]"
            onClick={onCancel}
            ref={cancelButtonRef}
          >
            <span>{cancelButtonLabel}</span>
          </CustomButton>
          <CustomButton
            color="primary"
            disabled={isDisabled}
            size="small"
            isFilled
            widthClass="w-[6.4375rem]"
            onClick={onApply}
            ref={applyButtonRef}
          >
            <span>{applyButtonLabel}</span>
          </CustomButton>
        </div>
      )}
    </div>
  )
}

export default PopupUiDropDownList
